import React from 'react';
import { Users, Coffee, Utensils, Activity, Building2, ChevronDown } from 'lucide-react';
import TeamMemberCard from './TeamMemberCard';

const TeamManagerDashboard = ({
  isTeamManager,
  managedTeams,
  selectedTeam,
  setSelectedTeam,
  teamMembers,
  teamMemberSessions,
  loadingTeamData,
  handleTeamMemberBreak,
  handleTeamMemberLunch,
  handleTeamMemberActivityChange,
  handleTeamMemberBusinessUnitChange,
  activities,
  businessUnits,
  handleTeamActivity,
  handleTeamBusinessUnit,
  isCompact = false
}) => {
  if (!isTeamManager) return null;

  const handleTeamChange = (e) => {
    const team = managedTeams.find(t => t.id === parseInt(e.target.value));
    setSelectedTeam(team);
  };

  const activeMembersCount = teamMembers.filter(member => 
    teamMemberSessions[member.employee_id]?.clock_in_time && 
    !teamMemberSessions[member.employee_id]?.clock_out_time
  ).length;

  const onBreakCount = teamMembers.filter(member => {
    const session = teamMemberSessions[member.employee_id];
    return session?.activity === 'BREAK' || session?.activity === 'Break';
  }).length;

  const onLunchCount = teamMembers.filter(member => {
    const session = teamMemberSessions[member.employee_id];
    return session?.activity === 'LUNCH' || session?.activity === 'Lunch';
  }).length;

  // Calculate if majority of active team members are on break/lunch for button state
  const activeMembers = teamMembers.filter(member => 
    teamMemberSessions[member.employee_id]?.clock_in_time && 
    !teamMemberSessions[member.employee_id]?.clock_out_time
  );
  
  const isTeamOnBreak = activeMembers.length > 0 && onBreakCount > 0;
  const isTeamOnLunch = activeMembers.length > 0 && onLunchCount > 0;

  return (
    <div className={`${isCompact ? '' : 'lg:col-span-3 bg-slate-800 border border-slate-700 rounded-lg shadow-sm p-6 mb-6'}`}>
      <div className={`flex justify-between items-center ${isCompact ? 'mb-4' : 'mb-6'}`}>
        <h2 className={`${isCompact ? 'text-lg' : 'text-xl'} font-semibold text-slate-200 flex items-center`}>
          <Users className={`${isCompact ? 'w-4 h-4' : 'w-5 h-5'} mr-2`} />
          Team Management
        </h2>
        
        {managedTeams.length > 1 && (
          <div className="relative">
            <select
              value={selectedTeam?.id || ''}
              onChange={handleTeamChange}
              className="bg-slate-700 text-slate-200 rounded-md px-4 py-2 pr-8 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {managedTeams.map(team => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
        )}
      </div>

      {/* Team Statistics */}
      <div className={`grid grid-cols-2 ${isCompact ? 'lg:grid-cols-4 gap-2 mb-4' : 'sm:grid-cols-4 gap-4 mb-6'}`}>
        <div className={`bg-slate-700/50 rounded-lg ${isCompact ? 'p-2' : 'p-3'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`${isCompact ? 'text-xs' : 'text-sm'} text-slate-400`}>Total</p>
              <p className={`${isCompact ? 'text-lg' : 'text-xl'} font-semibold text-slate-200`}>{teamMembers.length}</p>
            </div>
            <Users className={`${isCompact ? 'w-6 h-6' : 'w-8 h-8'} text-blue-400`} />
          </div>
        </div>

        <div className={`bg-slate-700/50 rounded-lg ${isCompact ? 'p-2' : 'p-3'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`${isCompact ? 'text-xs' : 'text-sm'} text-slate-400`}>Active</p>
              <p className={`${isCompact ? 'text-lg' : 'text-xl'} font-semibold text-green-400`}>{activeMembersCount}</p>
            </div>
            <Activity className={`${isCompact ? 'w-6 h-6' : 'w-8 h-8'} text-green-400`} />
          </div>
        </div>

        <div className={`bg-slate-700/50 rounded-lg ${isCompact ? 'p-2' : 'p-3'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`${isCompact ? 'text-xs' : 'text-sm'} text-slate-400`}>Break</p>
              <p className={`${isCompact ? 'text-lg' : 'text-xl'} font-semibold text-yellow-400`}>{onBreakCount}</p>
            </div>
            <Coffee className={`${isCompact ? 'w-6 h-6' : 'w-8 h-8'} text-yellow-400`} />
          </div>
        </div>

        <div className={`bg-slate-700/50 rounded-lg ${isCompact ? 'p-2' : 'p-3'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`${isCompact ? 'text-xs' : 'text-sm'} text-slate-400`}>Lunch</p>
              <p className={`${isCompact ? 'text-lg' : 'text-xl'} font-semibold text-orange-400`}>{onLunchCount}</p>
            </div>
            <Utensils className={`${isCompact ? 'w-6 h-6' : 'w-8 h-8'} text-orange-400`} />
          </div>
        </div>
      </div>

      {/* Team Actions */}
      <div className={`grid grid-cols-2 md:grid-cols-4 ${isCompact ? 'gap-2 mb-4' : 'gap-3 mb-6'}`}>
        <button
          onClick={() => handleTeamMemberBreak()}
          className={`flex items-center justify-center ${isCompact ? 'px-3 py-1.5 text-sm' : 'px-4 py-2'} ${
            isTeamOnBreak 
              ? 'bg-yellow-700 hover:bg-yellow-800' 
              : 'bg-yellow-600 hover:bg-yellow-700'
          } text-white rounded-md transition-colors`}
        >
          <Coffee className={`${isCompact ? 'w-3 h-3 mr-1' : 'w-4 h-4 mr-2'}`} />
          {isTeamOnBreak ? 'End Break' : 'Break'}
        </button>
        
        <button
          onClick={() => handleTeamMemberLunch()}
          className={`flex items-center justify-center ${isCompact ? 'px-3 py-1.5 text-sm' : 'px-4 py-2'} ${
            isTeamOnLunch 
              ? 'bg-orange-700 hover:bg-orange-800' 
              : 'bg-orange-600 hover:bg-orange-700'
          } text-white rounded-md transition-colors`}
        >
          <Utensils className={`${isCompact ? 'w-3 h-3 mr-1' : 'w-4 h-4 mr-2'}`} />
          {isTeamOnLunch ? 'End Lunch' : 'Lunch'}
        </button>

        {/* Team Activity Dropdown */}
        <div className="relative">
          <select
            onChange={(e) => handleTeamActivity(e.target.value)}
            className={`w-full flex items-center justify-center ${isCompact ? 'px-3 py-1.5 text-sm pr-7' : 'px-4 py-2 pr-8'} bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors cursor-pointer appearance-none`}
            defaultValue=""
          >
            <option value="" disabled>Activity</option>
            {activities.filter(activity => activity.is_active).map(activity => (
              <option key={activity.id} value={activity.name}>
                {activity.name}
              </option>
            ))}
          </select>
          <Activity className={`absolute ${isCompact ? 'right-2' : 'right-2'} top-1/2 transform -translate-y-1/2 ${isCompact ? 'w-3 h-3' : 'w-4 h-4'} pointer-events-none`} />
        </div>

        {/* Team Business Unit Dropdown */}
        <div className="relative">
          <select
            onChange={(e) => handleTeamBusinessUnit(e.target.value)}
            className={`w-full flex items-center justify-center ${isCompact ? 'px-3 py-1.5 text-sm pr-7' : 'px-4 py-2 pr-8'} bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors cursor-pointer appearance-none`}
            defaultValue=""
          >
            <option value="" disabled>Business Unit</option>
            {businessUnits.filter(unit => unit.is_active).map(unit => (
              <option key={unit.id} value={unit.identifier}>
                {unit.name}
              </option>
            ))}
          </select>
          <Building2 className={`absolute ${isCompact ? 'right-2' : 'right-2'} top-1/2 transform -translate-y-1/2 ${isCompact ? 'w-3 h-3' : 'w-4 h-4'} pointer-events-none`} />
        </div>
      </div>

      {/* Team Members */}
      <div className={`${isCompact ? 'space-y-3' : 'space-y-4'}`}>
        <h3 className={`${isCompact ? 'text-base' : 'text-lg'} font-medium text-slate-200`}>Team Members</h3>
        
        {loadingTeamData ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : teamMembers.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No team members found</p>
          </div>
        ) : (
          <div className={`grid grid-cols-1 ${isCompact ? 'gap-3' : 'lg:grid-cols-2 gap-4'}`}>
            {teamMembers.map(member => {
              const session = teamMemberSessions[member.employee_id];
              return (
                <TeamMemberCard
                  key={member.employee_id}
                  member={member}
                  session={session}
                  activities={activities}
                  businessUnits={businessUnits}
                  onActivityChange={handleTeamMemberActivityChange}
                  onBusinessUnitChange={handleTeamMemberBusinessUnitChange}
                  onBreakToggle={() => handleTeamMemberBreak(member.employee_id)}
                  onLunchToggle={() => handleTeamMemberLunch(member.employee_id)}
                  isCompact={isCompact}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamManagerDashboard;